import React, { useState, useRef, useEffect, useCallback } from 'react';
import Nav from './components/Nav';
import Hero from './components/Hero';
import Flywheel from './components/Flywheel';
import AgentBaseplates from './components/AgentBaseplates';
import SpeedRep from './components/SpeedRep';
import AuthorityBacklog from './components/AuthorityBacklog';
import OmnipresenceEngine from './components/OmnipresenceEngine';
import ValueProps from './components/ValueProps';
import SectionNav from './components/SectionNav';
import SideNav from './components/SideNav';

const CTA_URL = 'https://www.repupgrade.com/repupgradereportandplan/#repupgradeonboard';

const sections = [
  { id: 'hero', label: 'Home' },
  { id: 'flywheel', label: 'System' },
  { id: 'agents', label: 'AI Agents' },
  { id: 'speedrep', label: 'SpeedRep' },
  { id: 'authority', label: 'Authority' },
  { id: 'omnipresence', label: 'Omnipresence' },
  { id: 'value', label: 'Foundation' },
];

function App() {
  const [current, setCurrent] = useState(0);
  const isScrolling = useRef(false);
  const containerRef = useRef(null);

  const scrollTo = useCallback((index) => {
    if (index < 0 || index >= sections.length) return;
    setCurrent(index);
    isScrolling.current = true;
    setTimeout(() => { isScrolling.current = false; }, 800);
  }, []);

  const prev = useCallback(() => {
    if (current > 0) scrollTo(current - 1);
  }, [current, scrollTo]);

  const next = useCallback(() => {
    if (current < sections.length - 1) scrollTo(current + 1);
  }, [current, scrollTo]);

  // Handle Mouse Wheel / Trackpad Scroll
  useEffect(() => {
    let wheelTimer = null;
    const onWheel = (e) => {
      const slide = containerRef.current?.children[current];
      let canScrollNative = false;

      // Check if the current slide has scrollable content
      if (slide) {
        const isAtTop = slide.scrollTop <= 0;
        const isAtBottom = Math.ceil(slide.scrollTop + slide.clientHeight) >= slide.scrollHeight - 1;

        if (e.deltaY > 0 && !isAtBottom) canScrollNative = true; // Scrolling down, not at bottom
        if (e.deltaY < 0 && !isAtTop) canScrollNative = true;    // Scrolling up, not at top
      }

      if (canScrollNative) {
        return; // Allow native scrolling within the slide
      }

      // Reached the edge, prevent default bounce and trigger slide transition
      e.preventDefault();

      if (isScrolling.current) return;
      if (Math.abs(e.deltaY) < 30) return; // Ignore tiny trackpad movements

      clearTimeout(wheelTimer);
      wheelTimer = setTimeout(() => {
        if (e.deltaY > 0) next();
        else prev();
      }, 50);
    };

    const container = containerRef.current;
    if (container) container.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      if (container) container.removeEventListener('wheel', onWheel);
      clearTimeout(wheelTimer);
    };
  }, [current, next, prev]);

  // Handle Touch Swipe on Mobile
  useEffect(() => {
    let touchStartY = 0;
    const onTouchStart = (e) => { touchStartY = e.touches[0].clientY; };
    const onTouchEnd = (e) => {
      const diff = touchStartY - e.changedTouches[0].clientY;
      const slide = containerRef.current?.children[current];

      let isAtTop = true;
      let isAtBottom = true;

      if (slide) {
        isAtTop = slide.scrollTop <= 0;
        isAtBottom = Math.ceil(slide.scrollTop + slide.clientHeight) >= slide.scrollHeight - 1;
      }

      if (Math.abs(diff) > 50) {
        if (diff > 0 && isAtBottom) next(); // Swiped up, at bottom
        else if (diff < 0 && isAtTop) prev(); // Swiped down, at top
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('touchstart', onTouchStart, { passive: true });
      container.addEventListener('touchend', onTouchEnd, { passive: true });
    }
    return () => {
      if (container) {
        container.removeEventListener('touchstart', onTouchStart);
        container.removeEventListener('touchend', onTouchEnd);
      }
    };
  }, [current, next, prev]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowDown' || e.key === 'PageDown') next();
      if (e.key === 'ArrowUp' || e.key === 'PageUp') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [current, next, prev]);

  return (
    <div className="fixed inset-0 bg-[#202020] selection:bg-brand-teal selection:text-white">
      <Nav ctaUrl={CTA_URL} onNavClick={scrollTo} sections={sections} />
      <SectionNav sections={sections} current={current} onSelect={scrollTo} onPrev={prev} onNext={next} />
      <SideNav current={current} total={sections.length} onPrev={prev} onNext={next} />

      <div ref={containerRef} className="w-full h-full overflow-hidden relative">
        <Slide index={0} current={current}><Hero ctaUrl={CTA_URL} /></Slide>
        <Slide index={1} current={current}><Flywheel /></Slide>
        <Slide index={2} current={current}><AgentBaseplates /></Slide>
        <Slide index={3} current={current}><SpeedRep /></Slide>
        <Slide index={4} current={current}><AuthorityBacklog /></Slide>
        <Slide index={5} current={current}><OmnipresenceEngine /></Slide>
        <Slide index={6} current={current}><ValueProps ctaUrl={CTA_URL} /></Slide>
      </div>
    </div>
  );
}

function Slide({ index, current, children }) {
  const isActive = index === current;
  const isPrev = index < current;

  return (
    <div
      className={`absolute inset-0 w-full h-full overflow-y-auto overflow-x-hidden transition-transform duration-700 ease-in-out ${isActive ? 'z-10' : 'z-0 invisible'}`}
      style={{
        transform: isPrev ? 'translateY(-100%)' : isActive ? 'translateY(0%)' : 'translateY(100%)',
        willChange: 'transform',
        visibility: isActive ? 'visible' : 'hidden'
      }}
    >
      {/* Removed the centering wrapper here so sections can fill the screen with their backgrounds */}
      {children}
    </div>
  );
}

export default App;